import { create } from 'zustand';

import { ApiVideoCertificateResponse } from '@/shared/api';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';

export type SupportedDoc = { url: string; name: string; size: number };

type AttachmentId = string;

export type Attachment = {
  id: string;
  attachmentNumber: AttachmentId;
  filePath: string;
  createdAt: string;
};

export type WitnessDocument = Attachment & {
  name: string;
  size: number;
  signingDate: string;
};

export type VideoAttachment = Attachment & {
  duration: number;
  recordingDate: string;
  certificateId: string;
  generatedPdfPath: string;
};

export enum IwScenarioSettingsDetails {
  WillInstruction = 'WillInstruction',
  WillInstructionAsset = 'WillInstructionAsset',
}

export type IwScenarioSettings = {
  id?: string;
  status: 'draft' | 'active' | 'done';
  trigger: 'inactivity' | 'datetime' | null;
  transferDetails: IwScenarioSettingsDetails | null;
  periodicDays: number | null;
  datetime: string | null;
  beneficiaries: { dtoId: DtoId; centralProfileId: number }[];
};

export type VideoCertificate = ApiVideoCertificateResponse;

export type IwExecutionState = {
  willNumber: string | null;
  finalPdfUrl: string | null;
  signedWill: SupportedDoc | null;
  signingDate: string | null;
  signingPlace: string | null;
  storageLocation: string | null;
  executionDate: string | null;
  executionPlace: string | null;
  createdDate: string | null;
  supportedDocuments: SupportedDoc[];
  witnessDocuments: Record<DtoId, WitnessDocument>;
  decodedSignedWill: File | null;
  pdfPageCount: number;
  testatorVerification: VideoAttachment | null;
  witnessVerifications: Record<DtoId, VideoAttachment>;
  decodedTestatorVideo: Blob | null;
  decodedWitnessVideo: Record<DtoId, Blob | null>;
  videoCertificates: Record<AttachmentId, VideoCertificate>;
  scenarioSettings: IwScenarioSettings | null;
  willCertificatePath?: string;
};

type IwExecutionStore = IwExecutionState & {
  setWillNumber: (willNumber: string) => void;
  setFinalPdfUrl: (finalPdfUrl: string) => void;
  setSigningDate: (signingDate: string) => void;
  setSigningPlace: (signingPlace: string) => void;
  setStorageLocation: (storageLocation: string) => void;
  setSignedWill: (signedWill: SupportedDoc | null) => void;
  setExecutionDate: (executionDate: string) => void;
  setExecutionPlace: (executionPlace: string) => void;
  setSupportedDocuments: (supportedDocuments: SupportedDoc[]) => void;
  setCreatedDate: (createdDate: string) => void;
  setDecodedSignedWill: (decodedSignedWill: File | null) => void;
  setPdfPageCount: (pdfPageCount: number) => void;
  setTestatorVerification: (testatorVerification: VideoAttachment | null) => void;
  setWitnessVerifications: (witnessVerifications: Record<DtoId, VideoAttachment>) => void;
  setDecodedTestatorVideo: (decodedTestatorVideo: Blob | null) => void;
  setDecodedWitnessVideo: (decodedWitnessVideo: Record<DtoId, Blob | null>) => void;
  setVideoCertificates: (videoCertificates: Record<string, VideoCertificate>) => void;
  setScenarioSettings: (scenarioSettings: Partial<IwScenarioSettings> | null) => void;

  clear: () => void;
  setWitnessDocuments: (signs: Record<DtoId, WitnessDocument>) => void;
  extractStateForCollection: () => Omit<
    IwExecutionState,
    'decodedTestatorVideo' | 'decodedWitnessVideo' | 'decodedSignedWill'
  >;
  setWillCertificatePath: (willCertificatePath: string) => void;
};

const getInitialState = (): IwExecutionState => ({
  willNumber: null,
  finalPdfUrl: null,
  signingDate: null,
  signingPlace: null,
  storageLocation: null,
  signedWill: null,
  createdDate: null,
  executionDate: null,
  executionPlace: null,
  supportedDocuments: [],
  witnessDocuments: {},
  decodedSignedWill: null,
  pdfPageCount: 0,
  testatorVerification: null,
  witnessVerifications: {},
  decodedTestatorVideo: null,
  decodedWitnessVideo: {},
  videoCertificates: {},
  scenarioSettings: null,
});

export const useIwExecutionStore = create<IwExecutionStore>((set, get) => ({
  ...getInitialState(),

  setWillNumber: (willNumber) => set({ willNumber }),
  setFinalPdfUrl: (finalPdfUrl) => set({ finalPdfUrl }),
  setSigningDate: (signingDate) => set({ signingDate }),
  setSigningPlace: (signingPlace) => set({ signingPlace }),
  setStorageLocation: (storageLocation) => set({ storageLocation }),
  setSignedWill: (signedWill) => set({ signedWill }),
  setExecutionDate: (executionDate) => set({ executionDate }),
  setExecutionPlace: (executionPlace) => set({ executionPlace }),
  setSupportedDocuments: (supportedDocuments) => set({ supportedDocuments }),
  setCreatedDate: (createdDate) => set({ createdDate }),
  setWitnessDocuments: (witnessDocuments) => set({ witnessDocuments }),
  setDecodedSignedWill: (decodedSignedWill) => set({ decodedSignedWill }),
  setPdfPageCount: (pdfPageCount) => set({ pdfPageCount }),
  setTestatorVerification: (testatorVerification) => set({ testatorVerification }),
  setWitnessVerifications: (witnessVerifications) => set({ witnessVerifications }),
  setDecodedTestatorVideo: (decodedTestatorVideo) => set({ decodedTestatorVideo }),
  setDecodedWitnessVideo: (decodedWitnessVideo) => set({ decodedWitnessVideo }),
  setVideoCertificates: (videoCertificates) => set({ videoCertificates }),
  setScenarioSettings: (scenarioSettings) => {
    if (!scenarioSettings) {
      set({ scenarioSettings: null });
      return;
    }
    set((state) => ({
      scenarioSettings: {
        ...(state.scenarioSettings ?? {
          status: 'draft',
          trigger: null,
          transferDetails: null,
          periodicDays: null,
          datetime: null,
          beneficiaries: [],
        }),
        ...scenarioSettings,
      },
    }));
  },

  clear: () => {
    set(getInitialState());
  },

  setWillCertificatePath: (willCertificatePath) => set({ willCertificatePath }),

  extractStateForCollection: () => ({
    willNumber: get().willNumber,
    finalPdfUrl: get().finalPdfUrl,
    signingDate: get().signingDate,
    signingPlace: get().signingPlace,
    storageLocation: get().storageLocation,
    signedWill: get().signedWill,
    executionDate: get().executionDate,
    executionPlace: get().executionPlace,
    supportedDocuments: get().supportedDocuments,
    createdDate: get().createdDate,
    witnessDocuments: get().witnessDocuments,
    pdfPageCount: get().pdfPageCount,
    testatorVerification: get().testatorVerification,
    witnessVerifications: get().witnessVerifications,
    videoCertificates: get().videoCertificates,
    scenarioSettings: get().scenarioSettings,
    willCertificatePath: get().willCertificatePath,
  }),
}));
